import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import PrimaryActionButton from '../common/PrimaryActionButton';
import SalovareLoader from '../common/SalovareLoader';
import { buildAdornmentTextFieldSx, fieldLabelSx } from '../common/formFieldStyles';
import ProductService from '../../services/productService';
import { useToast } from '../../context/ToastContext';
import requestDeduplicator from '../../utils/requestDeduplicator';
import { BRAND } from '../../theme/brandTokens';

const ProductCommissionSettingsCard = ({
  storeId,
  canEdit,
  asTabPanel = false,
  initialSettings = null,
  settingsLoading = false,
  settingsError = null,
}) => {
  const { t } = useTranslation();
  const { showError, showSuccess } = useToast();
  const isManaged = asTabPanel;

  const [loading, setLoading] = useState(!isManaged);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [commissionPercent, setCommissionPercent] = useState('0');

  const displayLoading = isManaged ? settingsLoading : loading;
  const displayError = isManaged ? settingsError : loadError;
  const parsedPercent = useMemo(() => Number.parseFloat(commissionPercent), [commissionPercent]);
  const isPercentValid = Number.isFinite(parsedPercent) && parsedPercent >= 0 && parsedPercent <= 100;
  const showPercentError = commissionPercent.trim() !== '' && !isPercentValid;

  const loadSettings = useCallback(async () => {
    if (!storeId) {
      setLoading(false);
      return;
    }

    return requestDeduplicator.deduplicate(`product-settings-${storeId}`, async () => {
      try {
        setLoading(true);
        setLoadError(null);
        const settings = await ProductService.getProductSettings(storeId);
        setCommissionPercent(String(settings.productCommissionPercent ?? 0));
      } catch (error) {
        console.error('Failed to load product settings:', error);
        setLoadError('productsManagement.error.settings');
      } finally {
        setLoading(false);
      }
    });
  }, [storeId]);

  useEffect(() => {
    if (!isManaged || !initialSettings) {
      return;
    }

    setCommissionPercent(String(initialSettings.productCommissionPercent ?? 0));
  }, [initialSettings, isManaged]);

  useEffect(() => {
    if (isManaged) {
      return;
    }

    loadSettings();
  }, [isManaged, loadSettings]);

  const handleSave = async () => {
    if (!isPercentValid) {
      showError(t('productsManagement.error.settings'));
      return;
    }

    try {
      setSaving(true);
      await ProductService.updateProductSettings(storeId, {
        productCommissionPercent: parsedPercent,
      });
      showSuccess(t('productsManagement.feedback.settingsSaved'));
    } catch (error) {
      console.error('Failed to save product settings:', error);
      showError(t('productsManagement.error.settings'));
    } finally {
      setSaving(false);
    }
  };

  const settingsForm = displayLoading ? (
    <SalovareLoader size="small" center sx={{ py: 2 }} />
  ) : (
    <Stack spacing={2}>
      {displayError ? <Alert severity="error">{t(displayError)}</Alert> : null}

      <Box sx={{ maxWidth: 220 }}>
        <Typography variant="subtitle2" sx={fieldLabelSx}>
          {t('productsManagement.settings.commissionPercent')}
        </Typography>
        <TextField
          type="number"
          value={commissionPercent}
          disabled={!canEdit}
          onChange={(event) => setCommissionPercent(event.target.value)}
          inputProps={{
            min: 0,
            max: 100,
            step: '0.01',
            'aria-label': t('productsManagement.settings.commissionPercent'),
            'aria-invalid': showPercentError,
          }}
          InputProps={{
            endAdornment: <InputAdornment position="end">%</InputAdornment>,
          }}
          error={showPercentError}
          sx={buildAdornmentTextFieldSx(showPercentError)}
          fullWidth
        />
        {showPercentError ? (
          <Typography variant="caption" sx={{ color: '#f44336', mt: 0.5, display: 'block' }}>
            {t('productsManagement.settings.commissionPercentHelp')}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('productsManagement.settings.commissionPercentHelp')}
          </Typography>
        )}
      </Box>

      <Alert severity="info">
        {t('productsManagement.settings.selfServiceNote')}
      </Alert>

      {canEdit ? (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <PrimaryActionButton onClick={handleSave} disabled={saving || Boolean(displayError) || !isPercentValid}>
            {saving ? t('common.saving') : t('productsManagement.settings.save')}
          </PrimaryActionButton>
        </Box>
      ) : null}
    </Stack>
  );

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: BRAND.primaryText, mb: 0.5 }}>
          {t('productsManagement.settings.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('productsManagement.settings.description')}
        </Typography>
      </Box>
      {settingsForm}
    </Box>
  );
};

export default ProductCommissionSettingsCard;
